import { Box, useToken } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { IoEyeOutline } from 'react-icons/io5';
import { MdSupportAgent } from 'react-icons/md';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import { useGetAudit, useGetAudits } from '@/hooks/service-hooks/audit';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getAudits } from '@/services/audit';
import type { AuditRecord } from '@/types/audit';
import { buildEncryptedGetUrlAndHeaders } from '@/utils/buildWorkerExportRequest';
import { runFetchExportCsvWorker } from '@/utils/runDataExportWorker';
import type { FilterType } from '@/types/table';
import { generateExcelFromTable } from '@/utils/exportFiles';
import { SupportTicketForm } from '../../SupportTickets/SupportTicketForm';
import { AuditDetails } from '../AuditDetails';
import {
	formatDateValue,
	formatTimeValue,
	formatValue,
	moduleFilterOptions,
} from '../constants';
import { columns } from './columns';

const CLINIC_KEY = 'clinicId';
type AuditRecordWithLastAccessFallback = AuditRecord & {
	lastAccess?: Date | string | null;
};

const getClinicDisplayValue = (
	item: Pick<AuditRecord, 'clinicName' | 'userClinicNames'>,
	fallback: string,
) =>
	item.clinicName && item.clinicName !== 'N/A'
		? item.clinicName
		: item.userClinicNames || fallback;

const formatAuditLastActiveValue = (
	item: Pick<AuditRecordWithLastAccessFallback, 'lastActive' | 'lastAccess'>,
) => {
	const lastActiveValue = item.lastActive ?? item.lastAccess ?? null;

	if (!lastActiveValue) {
		return 'N/A';
	}

	const formattedDate = formatDateValue(lastActiveValue);
	const formattedTime = formatTimeValue(lastActiveValue);

	if (formattedDate === 'N/A' && formattedTime === 'N/A') {
		return 'N/A';
	}

	if (formattedDate === 'N/A') {
		return formattedTime;
	}

	if (formattedTime === 'N/A') {
		return formattedDate;
	}

	return `${formattedDate} ${formattedTime}`;
};

const AuditTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();

	// Clinic filter hook for global filters
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	const [_isExportingData, setIsExportingData] = useState<boolean>(false);

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: true,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicIds.length
				? [
						{
							key: CLINIC_KEY,
							values: defaultClinicIds,
						},
					]
				: [],
		),
		sort: '',
	});

	// Modal state management
	const [modalInformation, setModalInformation] = useState<{
		mode: 'details' | 'supportTicket' | null;
		data: number | null;
	}>({
		mode: null,
		data: null,
	});

	// Support ticket modal state
	const [supportTicketData, setSupportTicketData] = useState<{
		subject: string;
	} | null>(null);
	const [isSupportTicketFormDirty, setIsSupportTicketFormDirty] =
		useState(false);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	const { data, isFetching } = useGetAudits(filters);

	// Get individual audit data for details modal
	const { data: auditDetails, isFetching: isAuditDetailsLoading } = useGetAudit(
		modalInformation.mode === 'details'
			? (modalInformation.data as number)
			: null,
		modalInformation.mode === 'details',
	);

	// Transform API data to table format with N/A for empty values
	const transformedData = useMemo(() => {
		if (!data?.items) return [];

		return data.items.map((item) => {
			const clinicDisplay = getClinicDisplayValue(item, 'Multiple Clinics');

			return {
				id: item.id,
				userEmail: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.userEmail)}
						maxLength={25}
					/>
				),
				fullName: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.fullName)}
						maxLength={20}
					/>
				),
				module: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.module)}
						maxLength={20}
					/>
				),
				action: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.action)}
						maxLength={25}
					/>
				),
				entityName: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.entityName)}
						maxLength={22}
					/>
				),
				clinicName: (
					<FaceliftTruncatedTextWithTooltip
						text={clinicDisplay}
						maxLength={18}
					/>
				),
				ipAddress: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.ipAddress)}
						maxLength={15}
					/>
				),
				OS: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.OS)}
						maxLength={12}
					/>
				),
				platform: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.platform)}
						maxLength={12}
					/>
				),
				Port: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.Port)}
						maxLength={15}
					/>
				),
				userType: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.userType)}
						maxLength={15}
					/>
				),
				state: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.state)}
						maxLength={15}
					/>
				),
				country: (
					<FaceliftTruncatedTextWithTooltip
						text={formatValue(item.country)}
						maxLength={15}
					/>
				),
				lastActive: (
					<FaceliftTruncatedTextWithTooltip
						text={formatAuditLastActiveValue(item)}
						maxLength={22}
					/>
				),
				createdAt: (
					<FaceliftTruncatedTextWithTooltip
						text={formatDateValue(item.createdAt)}
						maxLength={18}
					/>
				),
				time: (
					<FaceliftTruncatedTextWithTooltip
						text={formatTimeValue(item.createdAt)}
						maxLength={12}
					/>
				),
			};
		});
	}, [data]);

	// Transform data for export (plain values without JSX)
	const transformedDataToExport = (items: AuditRecordWithLastAccessFallback[]) => {
		if (!items) return [];

		return items.map((item) => {
			return {
				id: item.id,
				userEmail: formatValue(item.userEmail),
				fullName: formatValue(item.fullName),
				module: formatValue(item.module),
				action: formatValue(item.action),
				entityName: formatValue(item.entityName),
				clinicName: getClinicDisplayValue(item, 'N/A'),
				ipAddress: formatValue(item.ipAddress),
				OS: formatValue(item.OS),
				platform: formatValue(item.platform),
				Port: formatValue(item.Port),
				userType: formatValue(item.userType),
				state: formatValue(item.state),
				country: formatValue(item.country),
				lastActive: formatAuditLastActiveValue(item),
				createdAt: formatDateValue(item.createdAt),
				time: formatTimeValue(item.createdAt),
			};
		});
	};

	// Export all audit data as CSV (HTTP + JSON parse + transform + CSV in Web Worker)
	const exportAllAsCSV = async () => {
		try {
			const { url, headers } = await buildEncryptedGetUrlAndHeaders('/audit', {
				page: 1,
				limit: 5000,
				columnsFilters: (filters.columnsFilters as string) || '[]',
				search: filters.search || '',
				status: (filters.status as string) || '',
				globalFilters: (filters.globalFilters as string) || '[]',
				sort: (filters.sort as string) || '',
			});

			await runFetchExportCsvWorker({
				url,
				headers,
				itemsPath: ['data', 'items'],
				rowTransform: 'audit',
				columns,
				docName: 'AuditLogs-all',
			});
			setIsExportingData(false);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
			setIsExportingData(false);
		}
	};

	// Export all audit data as Excel
	const exportAllAsExcel = async () => {
		try {
			setIsExportingData(true);
			const response = await getAudits(
				1,
				5000, // Max limit for export
				(filters.columnsFilters as string) || '[]',
				filters.search || '',
				(filters.status as string) || '',
				(filters.globalFilters as string) || '[]',
				(filters.sort as string) || '',
			);

			const transformedData = transformedDataToExport(response?.items || []);
			generateExcelFromTable(columns, transformedData, 'AuditLogs-all', true);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
			setIsExportingData(false);
		}
	};

	// Handle filter apply with clinic filter mapping and module status
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		// Map global filters: ensure clinicId filter is applied correctly
		const mappedFilters = mapGlobalFilters(globalFilters || []);

		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: true,
			status: status?.value || '',
			globalFilters: JSON.stringify(mappedFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Handle view audit details
	const handleViewDetails = (auditId: number) => {
		setModalInformation({
			mode: 'details',
			data: auditId,
		});
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
	};

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				py={{ base: 4, md: 6 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<DataTable
					tableName="Audit"
					loading={isFetching}
					items={transformedData}
					columns={columns}
					topLabel="Audit Logs"
					totalCount={data?.meta?.totalRecords || 0}
					onFilterApply={onFilterApply}
					globalFilter={{
						title: 'Filter',
						clinics: true,
					}}
					statusFilters={moduleFilterOptions}
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
					exportable
					expandable
					fullscreen
					paginated
					hideShowColumn
					excel
					searchable
					csv
					bordered
					actions={[
						{
							label: 'Details',
							icon: <IoEyeOutline />,
							actionFn: (item: ColumnData) => {
								const auditId = item.id as number;
								if (auditId) {
									handleViewDetails(auditId);
								}
							},
						},
						{
							label: 'Support Ticket',
							icon: <MdSupportAgent />,
							actionFn: (item: ColumnData) => {
								// Find the original audit data to get the full details
								const originalAudit = data?.items?.find(
									(audit) => audit.id === item.id,
								);

								// Create subject: Clinic Name (if exists) + Module Name + Action Name
								const clinicName =
									originalAudit?.clinicName &&
									originalAudit.clinicName !== 'N/A'
										? originalAudit.clinicName
										: null;
								const moduleName = originalAudit?.module || 'N/A';
								const actionName = originalAudit?.action || 'N/A';

								// Build subject conditionally based on clinic availability
								const subject = clinicName
									? `${clinicName} - ${moduleName} - ${actionName}`
									: `${moduleName} - ${actionName}`;

								setSupportTicketData({ subject });
								setIsSupportTicketFormDirty(false);
								setModalInformation({
									mode: 'supportTicket',
									data: item.id as number,
								});
							},
							show: () => hasPermission(['SupportTickets:Create']),
						},
					]}
				/>
			</Box>

			{/* Audit Details Modal */}
			<FaceliftDialog
				showBackBtn={true}
				title="Audit Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: '900px',
					maxWidth: '95vw',
					maxHeight: '95vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<Show when={isAuditDetailsLoading}>
					<FaceliftDataLoader
						title="Loading audit details..."
						description="Please wait while we fetch the audit information."
					/>
				</Show>
				<Show when={!isAuditDetailsLoading}>
					<AuditDetails data={auditDetails || null} />
				</Show>
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={modalInformation.mode === 'supportTicket'}
				onOpenChange={(openState: boolean) => {
					if (!openState) {
						setModalInformation({ mode: null, data: null });
						setSupportTicketData(null);
						setIsSupportTicketFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isSupportTicketFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode="create"
					initialData={{
						clinic: null,
						type: { value: '953870007', label: 'Tech' },
						subject: supportTicketData?.subject || '',
						status: { value: '1', label: 'In Progress' },
					}}
					onSuccess={() => {
						setModalInformation({ mode: null, data: null });
						setSupportTicketData(null);
						setIsSupportTicketFormDirty(false);
					}}
					onIsDirtyChange={setIsSupportTicketFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation.mode === 'supportTicket'
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>
		</>
	);
};

export default AuditTable;
